// App layout shell with header/nav and content region.
import { ReactNode } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { ConnectButton } from '@/components/wallet/ConnectButton'
import { TrendingUp, LayoutGrid, Briefcase, Plus } from 'lucide-react'

// Props contract for the layout wrapper.
interface LayoutProps {
  children: ReactNode
}

// Navigation definitions used in desktop + mobile menus.
const navItems = [
  { path: '/', label: 'Markets', icon: LayoutGrid },
  { path: '/trending', label: 'Trending', icon: TrendingUp },
  { path: '/portfolio', label: 'Portfolio', icon: Briefcase },
  { path: '/create', label: 'Create', icon: Plus },
]

export function Layout({ children }: LayoutProps) {
  // Current route for active link styling.
  const location = useLocation()

  return (
    <div className="min-h-screen bg-aurum-navy">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-aurum-navy/95 backdrop-blur-sm border-b border-aurum-gold/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-aurum-gold rounded-lg flex items-center justify-center">
                <span className="text-aurum-navy font-bold text-lg">A</span>
              </div>
              <span className="text-xl font-display font-bold text-white">Aurum</span>
            </Link>

            {/* Navigation */}
            <nav className="hidden md:flex items-center gap-1">
              {navItems.map((item) => {
                const Icon = item.icon
                const isActive = location.pathname === item.path
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                      isActive
                        ? 'bg-aurum-gold/10 text-aurum-gold'
                        : 'text-gray-400 hover:text-white hover:bg-white/5'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {item.label}
                  </Link>
                )
              })}
            </nav>

            {/* Wallet Connection */}
            <ConnectButton />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {children}
      </main>

      {/* Mobile Navigation */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-aurum-navy-light border-t border-aurum-gold/10 z-50">
        <div className="flex justify-around py-2">
          {navItems.map((item) => {
            const Icon = item.icon
            const isActive = location.pathname === item.path
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex flex-col items-center gap-1 px-4 py-2 ${
                  isActive ? 'text-aurum-gold' : 'text-gray-400'
                }`}
              >
                <Icon className="w-5 h-5" />
                <span className="text-xs">{item.label}</span>
              </Link>
            )
          })}
        </div>
      </nav>
    </div>
  )
}
